

import React, { ReactNode, useRef } from 'react';
import { PDFExport, PageTemplateProps } from '@progress/kendo-react-pdf';
import { DrawOptions, drawDOM, exportPDF } from '@progress/kendo-drawing';

import toastService from '../../../services/toastService';
import classNames from './style.module.scss';

export interface ExportParams {
    onDownload: () => Promise<void>;
    getBase64String: () => Promise<string | null>;
    isPublishing: boolean;
    pdfBreakClass: string;
}

interface Props {
    children: (params: ExportParams) => ReactNode;
    subject?: string;
    title?: string;
    fileName?: string;
    customPDFOptions?: DrawOptions
}

const PAGE_BREAK_CLASS = 'pdf-page';

const getDrawingPageTemplate = (props: PageTemplateProps): string => {
	return `<span style='
					padding: 20px; 
					position: absolute;
					bottom: 1rem;
					right: 1rem;'
			>
				${props.pageNum}  of ${props.totalPages}
			</span>`;
};

export const pdfOptions: DrawOptions = {
	scale: 0.5,
	paperSize: 'A4',
	forcePageBreak: '.' + PAGE_BREAK_CLASS,
	template: getDrawingPageTemplate,
};

const CustomPDFExport: React.FC<Props> = ({ children, customPDFOptions, subject, title, fileName }) => {
    const [isPublishing, setPublishing] = React.useState(false);

    const pdfExportComponent = React.useRef<PDFExport>(null);
    const elementRef = useRef<HTMLDivElement>(null);

    const onDownload = async () => {
        const pdf: PDFExport | null = pdfExportComponent.current;
        if (!pdf) return toastService.error('Document not available');

        setPublishing(true);
        pdf.save(() => setPublishing(false));
    };

    const getBase64String = async (): Promise<string | null> => {
        if (!elementRef.current) return null;

        setPublishing(true);

        const group = await drawDOM(elementRef.current, pdfOptions);
        const imageString: string = await exportPDF(group, pdfOptions);

        setPublishing(false);

        return imageString;
    };

    const getExportPageTemplate = (props: PageTemplateProps) => {
        return (
            <div className={classNames['page-number']}>
                {props.pageNum + ' of ' + props.totalPages}
            </div>
        );
    };

    return ( 
        <PDFExport
            {...pdfOptions}
            {...customPDFOptions}
            author="Arridex"
            subject={subject}
            title={title}
            producer="RSMG"
            fileName={fileName}
            ref={pdfExportComponent}
            pageTemplate={getExportPageTemplate}
        >
            <div ref={elementRef}>
                {children({ onDownload, isPublishing, getBase64String, pdfBreakClass: PAGE_BREAK_CLASS })}
            </div>
        </PDFExport>
     );
};
 
export default CustomPDFExport;